import { Link } from "react-router-dom";
import { BRAND, IMAGES, ELEMENTS } from "@/lib/brand";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BookingWidget } from "@/components/BookingWidget";
import { TrustBand } from "@/components/TrustBand";
import { GoogleReviewsSlider } from "@/components/GoogleReviewsSlider";
import { GoogleMapSection } from "@/components/GoogleMapSection";
import { Reveal } from "@/components/Reveal";
import { BrandSignature } from "@/components/BrandSignature";
import { ArrowUpRight, MountainSnow, Sparkles, Flame, Award } from "lucide-react";

export default function Home() {
  return (
    <div data-testid="page-home" className="bg-kc">
      {/* HERO — Official brand identity, single branding block */}
      <section className="relative min-h-[100vh] md:min-h-[760px] w-full overflow-hidden">
        <img src="/images/file_00000000b10481fab23dbb11b0b3234d.png" alt="Himalayan range" loading="eager" className="absolute inset-0 w-full h-full object-cover kc-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/25 to-black/60" />

        <div className="relative z-10 min-h-[100vh] md:min-h-[760px] max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-start md:justify-center pt-20 md:pt-28 pb-28 md:pb-16 text-[#F8F6F2]">
          {/* Service categories — what THE KC INDIA offers in one glance */}
          <div className="kc-fade-up" data-testid="hero-service-categories">
            <div className="font-cinzel text-[11px] md:text-[13px] uppercase tracking-[0.42em] text-gold-light flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>Hotels</span>
              <span className="w-1 h-1 rounded-full bg-gold/60" />
              <span>Travel</span>
              <span className="w-1 h-1 rounded-full bg-gold/60" />
              <span>Events</span>
              <span className="w-1 h-1 rounded-full bg-gold/60" />
              <span>Destination Weddings</span>
            </div>
          </div>

          {/* Main headline */}
          <h1 className="h-display kc-fade-up kc-delay-1 text-[42px] sm:text-[64px] md:text-[88px] lg:text-[108px] font-light leading-[0.98] max-w-5xl mt-3 md:mt-4 tracking-[-0.03em]" style={{textShadow:"0 3px 40px rgba(0,0,0,0.55)"}}>
            Celebrate Every Moment
          </h1>

          {/* Italic gold second line */}
          <p className="font-serif-italic kc-fade-up kc-delay-2 text-[26px] sm:text-[38px] md:text-[50px] lg:text-[60px] font-light leading-[1.05] mt-3 md:mt-4 tracking-[-0.01em] text-gold" style={{textShadow:"0 3px 40px rgba(0,0,0,0.55)"}}>
            in the Sacred Lap of the Himalaya.
          </p>

          {/* Supporting paragraph */}
          <p className="kc-fade-up kc-delay-3 mt-6 md:mt-10 max-w-2xl text-[14px] md:text-[16.5px] leading-[1.7] md:leading-[1.75] text-[#F8F6F2]/85">
            Experience luxury hospitality, peaceful mountain living, sacred journeys, and unforgettable celebrations in the divine land of Lord Shiva. Every stay is thoughtfully designed to create memories that last forever.
          </p>

          {/* Premium brand keywords — hidden on mobile for compact layout */}
          <div className="kc-fade-up kc-delay-4 mt-8 hidden md:flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.32em] text-[#F8F6F2]/70">
            {["Luxury Hospitality", "Spiritual Journeys", "Nature", "Comfort", "Divinity"].map((k, i, arr) => (
              <span key={k} className="flex items-center gap-3">
                <span>{k}</span>
                {i < arr.length - 1 && <span className="w-1 h-1 rounded-full bg-gold" />}
              </span>
            ))}
          </div>

          {/* CTAs — Book Your Stay + 5 outlined premium buttons */}
          <div className="mt-6 md:mt-10 kc-fade-up kc-delay-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2.5 md:gap-3 max-w-6xl">
            <Link to="/book" className="btn-gold justify-center !py-3 md:!py-4" data-testid="hero-book-stay">
              Book Your Stay <ArrowUpRight size={14} />
            </Link>
            <Link to="/book/yatra" className="btn-ghost-light justify-center !py-3 md:!py-4" data-testid="hero-book-yatra">
              Plan Your Yatra
            </Link>
            <Link to="/book/wedding" className="btn-ghost-light justify-center !py-3 md:!py-4" data-testid="hero-book-wedding">
              Plan Your Wedding
            </Link>
            <Link to="/book/event" className="btn-ghost-light justify-center !py-3 md:!py-4" data-testid="hero-book-event">
              Book an Event
            </Link>
            <Link to="/book/transport" className="btn-ghost-light justify-center !py-3 md:!py-4" data-testid="hero-book-transport">
              Book a Vehicle
            </Link>
            <Link to="/experiences" className="btn-ghost-light justify-center !py-3 md:!py-4" data-testid="hero-explore-experiences">
              Explore Experiences
            </Link>
          </div>
        </div>
      </section>

      {/* BOOKING WIDGET — sits at hero/content boundary */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 -mt-16 relative z-30">
        <Reveal>
          <BookingWidget />
        </Reveal>
        <Reveal delay={200} className="mt-10 flex justify-center">
          <BrandSignature size="sm" tone="ink" align="center" data-testid="booking-brand-signature" />
        </Reveal>
      </section>

      {/* TRUST BAND */}
      <div className="mt-16 md:mt-24">
        <TrustBand />
      </div>

      {/* INTRO — mission statement, editorial */}
      <section className="section-pad overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-3 min-w-0">
            <div className="overline">The House</div>
            <p className="mt-8 text-[11px] uppercase tracking-[0.32em] text-ink-mute">A Note From Sersi</p>
            <div className="mt-4 index-num">— 2026</div>
          </Reveal>
          <Reveal delay={150} className="md:col-span-9 min-w-0">
            <p className="font-serif-display text-2xl md:text-4xl lg:text-5xl font-light leading-[1.2] text-ink tracking-[-0.02em] break-words">
              While others compete in luxury,<br />
              THE KC INDIA creates <span className="italic text-gold">divine hospitality</span> — through peaceful stays, curated pilgrimage journeys, and weddings that carry the blessing of the mountains.
            </p>
            <div className="mt-12 hairline-gold w-1/3" />
          </Reveal>
        </div>
      </section>

      {/* THREE JOURNEYS — editorial luxury cards */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pb-24">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-6">
              <div className="overline">Three Journeys</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">One house. Three sacred journeys.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-8 text-ink-soft text-[15px] leading-relaxed max-w-md">
              Every guest arrives with a different reason to travel. We meet each one with equal reverence — a stay, a yatra, or a wedding curated with quiet care.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {[
            { n: "01", to: "/stay",     tag: "Stay",       title: "Rooms that rest the soul.",  desc: "Boutique cottage suites with mountain windows.", img: IMAGES.roomLuxury,   span: "md:col-span-7 h-[440px] md:h-[500px]" },
            { n: "02", to: "/chardham", tag: "Char Dham",  title: "A pilgrimage, curated.",     desc: "Kedarnath, Badrinath, Gangotri, Yamunotri.",     img: IMAGES.heroKedarnath, span: "md:col-span-5 h-[440px] md:h-[500px]" },
            { n: "03", to: "/weddings/triyuginarayan", tag: "Weddings",   title: "Vows blessed by mountains.", desc: "Triyuginarayan · Where Shiva & Parvati exchanged their vows.", img: IMAGES.mandap,       span: "md:col-span-12 h-[440px] md:h-[500px]" },
          ].map((c, i) => (
            <Reveal delay={i * 120} key={c.n} className={`group relative img-zoom overflow-hidden ${c.span}`}>
              <Link to={c.to} className="block w-full h-full" data-testid={`home-card-${c.tag.toLowerCase().replace(" ", "")}`}>
                <img src={c.img} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-between text-[#F8F6F2]">
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] uppercase tracking-[0.42em] text-[#D4AF37]">№ {c.n} · {c.tag}</span>
                    <ArrowUpRight size={20} className="text-[#D4AF37] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <div>
                    <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl font-light leading-[1.05] max-w-md tracking-[-0.02em]">{c.title}</h3>
                    <p className="mt-4 text-[13px] text-[#F8F6F2]/75 max-w-sm">{c.desc}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO — deep black cinematic */}
      <section className="bg-[#0F0F0F] text-[#F8F6F2] section-pad relative overflow-hidden">
        <img src={IMAGES.himalayas} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <Reveal className="md:col-span-3 min-w-0">
              <div className="overline text-[#D4AF37]">Manifesto</div>
              <p className="mt-8 vertical-label hidden md:block h-32">Atithi Devo Bhava</p>
            </Reveal>
            <Reveal delay={150} className="md:col-span-9 min-w-0">
              <div className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.25] font-light tracking-[-0.02em] break-words">
                We don't sell rooms.<br />
                We offer <span className="italic text-[#D4AF37]">rest</span>.<br />
                We don't sell packages.<br />
                We create <span className="italic text-[#D4AF37]">journeys</span>.<br />
                We don't build hotels.<br />
                We nurture <span className="italic text-[#D4AF37]">experiences</span>.
              </div>
              <div className="mt-12 hairline-gold w-1/3" />
              <p className="font-devanagari text-2xl text-[#D4AF37] mt-10">अतिथि देवो भव</p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.32em] text-[#F8F6F2]/60">The Guest is God — Our Way of Hospitality</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PANCHA MAHABHUTA */}
      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
              <div className="md:col-span-6">
                <div className="overline">Pancha Mahabhuta</div>
                <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">
                  Five elements.<br />One <span className="italic text-gold">divine</span> hospitality.
                </h2>
              </div>
              <p className="md:col-span-5 md:col-start-8 text-ink-soft text-[15px] leading-relaxed">
                Every experience at THE KC INDIA is composed from the same timeless materials — the elements that hold the Himalayas together.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#0F0F0F]/10">
            {ELEMENTS.map((e, i) => (
              <Reveal delay={i * 90} key={e.name} className="bg-kc p-8 md:p-10">
                <div className="index-num">№ 0{i + 1}</div>
                <div className="font-devanagari text-3xl text-gold mt-6">{e.hi}</div>
                <div className="font-serif-display text-3xl mt-2 text-ink">{e.name}</div>
                <div className="text-ink-mute text-[11px] uppercase tracking-[0.28em] mt-3">{e.meaning}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY KC INDIA */}
      <section className="bg-cream section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
          <Reveal className="md:col-span-4">
            <div className="overline">The House Standards</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">A promise you can trust.</h2>
            <p className="mt-6 text-ink-soft text-[14px] leading-relaxed">Every detail, from the first call to the final farewell, is held with the same reverence.</p>
          </Reveal>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-12 gap-y-14">
            {[
              { n: "01", icon: MountainSnow, t: "Steps from Heritage Aviation", d: "Moments from the Kedarnath helicopter route in Sersi, Rudraprayag." },
              { n: "02", icon: Sparkles,     t: "Atithi Devo Bhava",           d: "Warm, quiet, and personal hospitality — every guest is family." },
              { n: "03", icon: Flame,        t: "Divine Kitchen",              d: "Warm, sattvic Himalayan meals prepared with reverence." },
              { n: "04", icon: Award,        t: "24 × 7 Yatra Assistance",     d: "Weather, road, helicopter and taxi guidance whenever you need it." },
            ].map((f, i) => (
              <Reveal delay={i * 120} key={f.n} className="border-t border-kc pt-6">
                <div className="flex items-baseline gap-6 mb-4">
                  <span className="index-num">№ {f.n}</span>
                  <f.icon size={18} className="text-gold" />
                </div>
                <div className="font-serif-display text-2xl text-ink leading-tight">{f.t}</div>
                <p className="text-ink-soft text-[13.5px] mt-3 leading-relaxed">{f.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GUEST STORY */}
      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="kc-ornament max-w-md mx-auto">A Guest Reflection</div>
            <blockquote className="font-serif-italic text-2xl md:text-4xl leading-[1.35] mt-12 text-ink font-light tracking-[-0.01em]">
              "We didn't come to a hotel. We came to a moment we will carry for life — the sound of the river, the warmth of the lamp, the calm of the mountains."
            </blockquote>
            <div className="mt-10 text-[10px] uppercase tracking-[0.42em] text-ink-mute">A pilgrim family · Kedarnath Yatra</div>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS SLIDER — live-verified 4.9★ Google Business Profile */}
      <GoogleReviewsSlider />

      {/* MAP — official Google Maps listing with directions, satellite, zoom */}
      <GoogleMapSection />

      {/* CTA COMPOSITION */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-5 p-10 md:p-14 bg-[#0F0F0F] text-[#F8F6F2] relative overflow-hidden">
            <div className="overline text-[#D4AF37]">Begin</div>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-6 font-light leading-[1.15] tracking-[-0.02em]">Your Himalayan journey begins with one <span className="italic text-[#D4AF37]">conversation</span>.</h3>
            <p className="text-[#F8F6F2]/70 mt-6 text-[14px] leading-relaxed max-w-md">Speak to our travel specialist. No pressure. Only guidance.</p>
            <Link to="/contact" className="btn-gold mt-12" data-testid="cta-contact">Talk to a Specialist <ArrowUpRight size={14} /></Link>

            <div className="mt-16 pt-6 border-t border-[#D4AF37]/25 grid grid-cols-2 gap-6 text-[11px] uppercase tracking-[0.24em] text-[#F8F6F2]/60">
              <div>
                <div className="text-[#D4AF37]">Call</div>
                <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="text-[#F8F6F2] mt-2 block normal-case tracking-normal text-[14px]" data-testid="cta-phone">{BRAND.phone}</a>
              </div>
              <div>
                <div className="text-[#D4AF37]">Write</div>
                <a href={`mailto:${BRAND.email}`} className="text-[#F8F6F2] mt-2 block normal-case tracking-normal text-[14px]" data-testid="cta-email">{BRAND.email}</a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150} className="md:col-span-7"><EnquiryForm type="stay" /></Reveal>
        </div>
      </section>
    </div>
  );
}
